// Third-party Library
import { useState } from "react"
import { useDispatch, useSelector } from "react-redux"
import { Card, Divider, Input, Row, Col, Checkbox, Button, message, Typography } from "antd"

// Constants
import { TEMPLATE } from "../../../../../v3/constants"

// Services Library
import { TIME } from "../../../../../v3/services/util"

// Handlers
import { mileageKMConvertor } from "../../../../../v3/handlers/report"

// Actions Library
import { create_driver_pretip_request } from "../../../../../v3/services/redux/actions/drivers"

// Styles
import styles from './index.module.css'

const { Text } = Typography

const {
    parseDate,
    getCurTime
} = TIME

const {
    DRIVER_PRETRIP_CHECKLIST_CATEGORY_LABEL,
    DRIVER_PRETRIP_CHECKLIST_CATEGORY_LABEL_MALAY,
    DRIVER_PRETRIP_CHECKLIST_CATEGORY_CODES,
} = TEMPLATE.DRIVER

const {
    PRETRIP_WORDINGS
} = TEMPLATE.PRETRIP

const SECTION_LETTERS = ['B', 'C', 'D', 'E', 'F', 'G', 'H', 'I', 'J']

const InfoComponent = ({ title, info, span = 8 }) => (
    <Col span={span} style={{ display: 'flex' }}>
        <Divider type="vertical" className={styles.divider} />
        <div>
            <h5 style={{ marginBottom: 2, color: '#8c8c8c', fontSize: 11 }}>{title}</h5>
            <h3 style={{ margin: 0, fontSize: 14 }}>{info || '-'}</h3>
        </div>
    </Col>
)

const YaNot = ({ itemid, value, onChange, language }) => {
    const yesLabel = language === 'EN' ? 'Yes' : 'Ya'
    const noLabel  = language === 'EN' ? 'No'  : 'Tidak'

    return (
        <div className={styles.yaNot}>
            <label
                className={`${styles.yaNotItem} ${value === 'YES' ? styles.yaNotYesActive : styles.yaNotYes}`}
                onClick={() => onChange(itemid, 'YES')}
                aria-hidden="true"
            >
                <span className={`${styles.yaNotDot} ${value === 'YES' ? styles.yaNotDotYesActive : styles.yaNotDotYes}`} />
                {yesLabel}
            </label>
            <label
                className={`${styles.yaNotItem} ${value === 'NO' ? styles.yaNotNoActive : styles.yaNotNo}`}
                onClick={() => onChange(itemid, 'NO')}
                aria-hidden="true"
            >
                <span className={`${styles.yaNotDot} ${value === 'NO' ? styles.yaNotDotNoActive : styles.yaNotDotNo}`} />
                {noLabel}
            </label>
        </div>
    )
}

const ChecklistTable = ({ itemIDs, driverPretripChecklists, language, radioAnswers, onRadioChange }) => {
    const bilLabel    = language === 'EN' ? 'No.'    : 'Bil.'
    const itemLabel   = language === 'EN' ? 'Check Item'          : 'Perkara Pemeriksaan'
    const choiceLabel = language === 'EN' ? 'Choice'              : 'Pilihan'

    return (
        <table className={styles.table}>
            <thead>
                <tr className={styles.thead}>
                    <th className={styles.thBil}>{bilLabel}</th>
                    <th className={styles.thItem}>{itemLabel}</th>
                    <th className={styles.thChoice}>{choiceLabel}</th>
                </tr>
            </thead>
            <tbody>
                {itemIDs.map((itemid, idx) => {
                    const item  = driverPretripChecklists.byID[itemid]
                    const label = language === 'EN' ? item?.item : item?.itemMalay

                    return (
                        <tr key={itemid} className={styles.tr}>
                            <td className={styles.tdBil}>{idx + 1}</td>
                            <td className={styles.tdItem}>{label}</td>
                            <td className={styles.tdChoice}>
                                <YaNot
                                    itemid={itemid}
                                    value={radioAnswers[itemid]}
                                    onChange={onRadioChange}
                                    language={language}
                                />
                            </td>
                        </tr>
                    )
                })}
            </tbody>
        </table>
    )
}

const SectionHeader = ({ letter, title }) => (
    <div className={styles.sectionHeader}>
        <span className={styles.sectionLetter}>{letter}</span>
        <span className={styles.sectionTitle}>{title}</span>
    </div>
)

const TripChecklistForm = ({
    vehicle = {},
    setToForm,
    setSuccess,
    driver = {},
    language,
    plate
}) => {

    const dispatch = useDispatch()
    const driverPretripChecklists = useSelector(state => state.containers.v3.driverPretripChecklists)
    // const driverPretripChecklists = DRIVER_PRETRIP_CHECKLIST_DUMMY

    const [confirm, setConfirm]   = useState(false)
    const [desc, setDesc]         = useState('')
    const [radioAnswers, setRadioAnswers] = useState(Object.fromEntries(driverPretripChecklists.allIDs?.map(id => [id, 'YES']) ?? []))

    const {
        driver: ldriver,
        plate: lplate,
        date: ldate,
        vytype: lvtype,
        check_title,
        check_confirm
    } = PRETRIP_WORDINGS[language]

    const onRadioChange = (itemid, value) =>
        setRadioAnswers(prev => ({ ...prev, [itemid]: value }))

    const onSubmit = () => {
        if (!confirm) {
            message.error('You have to confirm the filled form!')
            return
        }
        const checkedItems = Object.entries(radioAnswers)
            .filter(([, val]) => val === 'YES')
            .map(([id]) => id)

        dispatch(create_driver_pretip_request({
            vid: vehicle.vid,
            drvid: driver.drvid,
            items: checkedItems,
            note: desc,
            odometerStart: vehicle.odometer
        }, () => {
            setToForm(false)
            setSuccess(true)
        }))
    }

    return (
        <div style={{ padding: '0.6em 1.5em' }}>

            {/* Driver Info */}
            <Card style={{ marginBottom: '1em' }}>
                <Row gutter={10}>
                    <InfoComponent title={ldriver} info={driver.name} span={24} />
                </Row>
                <Row gutter={10} style={{ marginTop: 12 }}>
                    <InfoComponent span={6} title={lplate} info={plate} />
                    <InfoComponent span={6} title={ldate} info={parseDate(getCurTime())} />
                    <InfoComponent span={6} title={lvtype} info={vehicle.type} />
                    <InfoComponent span={6} title="Odometer" info={vehicle.odometer ? `${mileageKMConvertor(vehicle.odometer)?.toFixed(0)?.toLocaleString('en-US')} km` : '-'} />
                </Row>
            </Card>

            {/* Section A: Odometer */}
            <Card style={{ marginBottom: '1em' }} bodyStyle={{ padding: 0 }}>
                <SectionHeader letter="A" title={language === 'EN' ? 'Odometer Before Trip' : 'Meter Sebelum Perjalanan'} />
                <div style={{ padding: '1em 1.25em' }}>
                    <div style={{ marginBottom: 12 }}>
                        <label className={styles.fieldLabel}>
                            {language === 'EN' ? 'Odometer Reading Before Trip' : 'Bacaan Meter Sebelum Perjalanan'}
                            <span style={{ color: '#ff4d4f', marginLeft: 4 }}>*</span>
                        </label>
                        <Input
                            placeholder={language === 'EN' ? 'e.g. 3828' : 'Contoh: 3828'}
                            suffix={<Text type="secondary" style={{ fontSize: 12 }}>KM</Text>}
                            defaultValue={vehicle.odometer ? mileageKMConvertor(vehicle.odometer)?.toFixed(0)?.toLocaleString('en-US') : ''}
                        />
                    </div>
                    <div>
                        <label className={styles.fieldLabel}>
                            {language === 'EN' ? 'Trip Start Time' : 'Masa Sebelum Perjalanan'}
                        </label>
                        <Input
                            disabled
                            placeholder={language === 'EN' ? 'Auto-filled when submitted' : 'Direkod automatik semasa hantar'}
                        />
                    </div>
                </div>
            </Card>

            {/* Checklist Sections (B, C, ...) */}
            {DRIVER_PRETRIP_CHECKLIST_CATEGORY_CODES?.map((category, i) => {
                const title = language === 'EN'
                    ? DRIVER_PRETRIP_CHECKLIST_CATEGORY_LABEL[category]
                    : DRIVER_PRETRIP_CHECKLIST_CATEGORY_LABEL_MALAY[category]

                const itemIDs = driverPretripChecklists.byCategory[category] || []

                return (
                    <Card key={category} style={{ marginBottom: '1em' }} bodyStyle={{ padding: 0 }}>
                        <SectionHeader letter={SECTION_LETTERS[i]} title={title} />
                        <ChecklistTable
                            itemIDs={itemIDs}
                            driverPretripChecklists={driverPretripChecklists}
                            language={language}
                            radioAnswers={radioAnswers}
                            onRadioChange={onRadioChange}
                        />
                    </Card>
                )
            })}

            {/* Nota Pemandu */}
            <Card style={{ marginBottom: '1em' }} bodyStyle={{ padding: 0 }}>
                <SectionHeader title={language === 'EN' ? 'DRIVER NOTES' : 'NOTA PEMANDU'} />
                <table className={styles.table}>
                    <tbody>
                        <tr className={styles.tr}>
                            <td colSpan={2} style={{ padding: '10px 12px' }}>
                                <Input.TextArea
                                    onChange={e => setDesc(e.target.value)}
                                    rows={3}
                                    placeholder={language === 'EN' ? 'Write notes here...' : 'Tulis nota atau catatan pemeriksaan di sini...'}
                                    style={{ resize: 'none' }}
                                />
                            </td>
                        </tr>
                    </tbody>
                </table>
            </Card>

            {/* Pengesahan Pemandu */}
            <Card style={{ marginBottom: '1em' }} bodyStyle={{ padding: 0 }}>
                <SectionHeader title={language === 'EN' ? 'DRIVER CONFIRMATION' : 'PENGESAHAN PEMANDU'} />
                <div style={{ padding: '1em 1.25em' }}>
                    <p style={{ fontSize: 13, color: '#555', marginBottom: 12 }}>
                        {check_title}
                    </p>
                    <Checkbox onChange={e => setConfirm(e.target.checked)} value={confirm} style={{ fontSize: 13 }}>
                        {check_confirm}
                    </Checkbox>
                    <Row gutter={16} style={{ marginTop: 16, paddingTop: 16, borderTop: '1px solid #f0f0f0' }}>
                        <Col span={12}>
                            <label className={styles.fieldLabel}>
                                {language === 'EN' ? 'Signed by Driver' : 'Disemak oleh Pemandu'}
                            </label>
                            <p style={{ fontWeight: 700, fontSize: 14, margin: 0 }}>{driver.name || '-'}</p>
                        </Col>
                        <Col span={12}>
                            <label className={styles.fieldLabel}>
                                {language === 'EN' ? 'Driver Signature' : 'Tandatangan Pemandu'}
                            </label>
                            <Input placeholder={language === 'EN' ? 'Enter name / signature' : 'Masukkan nama / tandatangan'} />
                        </Col>
                    </Row>
                </div>
            </Card>

            <Button type="primary" block size="large" onClick={onSubmit} className={styles.submitBtn}>
                {language === 'EN' ? 'Submit Inspection' : 'Hantar Pemeriksaan'}
            </Button>

        </div>
    )
}

export default TripChecklistForm